import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, HeartHandshake, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CircleAvatar } from "@/components/CircleAvatar";
import { api } from "@/lib/api";
import { startCheckout } from "@/lib/checkout";
import { createCheckoutExternal, stashExternalPending, EXTERNAL_FEE } from "@/lib/external";
import { normRecipient, tierColor, money, PRESET_AMOUNTS, platformFee } from "@/lib/blessed";

export default function DonatePage() {
  const { recipientId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fundraiserId = params.get("fundraiser_id");

  const [recipient, setRecipient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState(Number(params.get("amount")) || 20);
  const [custom, setCustom] = useState("");
  const [message, setMessage] = useState(params.get("message") || "");
  const [anonymous, setAnonymous] = useState(false);
  const [sending, setSending] = useState(false);
  const [method, setMethod] = useState("stripe");

  useEffect(() => {
    api
      .get(`/recipients/${recipientId}`)
      .then(({ data }) => setRecipient(normRecipient(data)))
      .catch(() => toast.error("Could not load this neighbor."))
      .finally(() => setLoading(false));
  }, [recipientId]);

  const effectiveAmount = custom ? Number(custom) : amount;
  const fee = useMemo(() => platformFee(effectiveAmount), [effectiveAmount]);
  const hasZelle = (recipient?.payoutMethods || []).includes("zelle") || !!(recipient?.zelleQr || recipient?.zelleHandle);
  const hasVenmo = (recipient?.payoutMethods || []).includes("venmo") || !!(recipient?.venmoQr || recipient?.venmoHandle);
  const isExternal = method === "zelle" || method === "venmo";

  const send = async () => {
    if (!effectiveAmount || effectiveAmount < 5) {
      toast.error("The minimum blessing is $5.");
      return;
    }
    setSending(true);
    try {
      if (isExternal) {
        const { data } = await createCheckoutExternal({
          recipient_id: recipientId,
          amount: effectiveAmount,
          method,
          message: message.trim() || undefined,
          anonymous,
        });
        stashExternalPending(data?.donation_id);
        const url = data?.checkout_url || data?.url;
        if (url) { toast.success("Opening secure checkout…"); window.location.href = url; }
        else toast.error("Checkout link unavailable.");
      } else {
        const body = {
          recipient_id: recipientId,
          amount: effectiveAmount,
          message: message.trim() || null,
          anonymous,
        };
        if (fundraiserId) body.fundraiser_id = fundraiserId;
        const { url } = await startCheckout(body);
        if (url) { toast.success("Opening secure checkout…"); window.location.href = url; }
        else toast.error("Checkout link unavailable.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-brand-primary" size={34} /></div>;
  }
  if (!recipient) {
    return <div className="p-8 text-center text-ink-tertiary">Neighbor not found.</div>;
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-5 pb-28">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-ink-tertiary mb-4" data-testid="donate-back">
        <ArrowLeft size={18} /> Back
      </button>

      {/* Hero */}
      <div className="rounded-lg overflow-hidden border border-border bg-surface-2 mb-6">
        <div className="h-24" style={{ background: "linear-gradient(120deg,#1E40AF,#5B21B6)" }} />
        <div className="px-5 pb-5 -mt-10">
          <CircleAvatar src={recipient.picture} name={recipient.name} size={80} ring="linear-gradient(135deg,#FFD93D,#FF8A3D)" className="border-4 border-surface-2" />
          <h1 className="text-2xl font-black mt-3">{recipient.name}</h1>
          {recipient.distance != null && <p className="text-sm font-semibold text-ink-tertiary">{recipient.distance.toFixed(1)} mi away</p>}
          {recipient.story && <p className="text-sm text-ink-tertiary leading-relaxed mt-3">{recipient.story}</p>}
          {fundraiserId && <p className="text-xs font-bold text-brand-secondary mt-3">Your gift will credit their fundraiser 🎉</p>}
        </div>
      </div>

      {/* Payment method */}
      {(hasZelle || hasVenmo) && (
        <div className="mb-5" data-testid="method-picker">
          <div className="text-sm font-black mb-2">How do you want to send?</div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="method-stripe" onClick={() => setMethod("stripe")}
              className={`px-4 py-2 rounded-pill text-sm font-black border transition ${method === "stripe" ? "bg-brand-primary text-white border-brand-primary" : "bg-surface-2 border-border text-ink"}`}>
              Card (Stripe)
            </button>
            {hasZelle && (
              <button data-testid="method-zelle" onClick={() => setMethod("zelle")}
                className={`px-4 py-2 rounded-pill text-sm font-black border transition ${method === "zelle" ? "bg-brand-primary text-white border-brand-primary" : "bg-surface-2 border-border text-ink"}`}>
                Zelle · ${EXTERNAL_FEE} fee
              </button>
            )}
            {hasVenmo && (
              <button data-testid="method-venmo" onClick={() => setMethod("venmo")}
                className={`px-4 py-2 rounded-pill text-sm font-black border transition ${method === "venmo" ? "bg-brand-primary text-white border-brand-primary" : "bg-surface-2 border-border text-ink"}`}>
                Venmo · ${EXTERNAL_FEE} fee
              </button>
            )}
          </div>
        </div>
      )}

      {/* Amount pills */}
      <div className="text-sm font-black mb-2">Choose an amount</div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {PRESET_AMOUNTS.map((a) => {
          const active = !custom && amount === a;
          return (
            <button
              key={a}
              data-testid={`donate-amount-${a}`}
              onClick={() => { setAmount(a); setCustom(""); }}
              className={`py-3 rounded-md font-black text-white transition-transform ${active ? "scale-105 ring-2 ring-offset-2 ring-offset-surface ring-ink" : "opacity-90"}`}
              style={{ background: tierColor(a) }}
            >
              {money(a)}
            </button>
          );
        })}
      </div>
      <Input
        data-testid="donate-custom-amount"
        type="number"
        value={custom}
        onChange={(e) => setCustom(e.target.value)}
        placeholder="Custom amount ($)"
        className="bg-surface-2 border-border mb-4"
      />

      <Textarea
        data-testid="donate-message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Add a kind message (optional)"
        autoFocus={!!params.get("message")}
        className="bg-surface-2 border-border mb-4"
      />

      <label className="flex items-center justify-between bg-surface-2 border border-border rounded-md px-4 py-3 mb-4 cursor-pointer">
        <span className="font-bold text-sm">Give anonymously</span>
        <Switch data-testid="donate-anonymous" checked={anonymous} onCheckedChange={setAnonymous} />
      </label>

      {/* Fee breakdown */}
      {isExternal ? (
        <div className="bg-surface-2 border border-border rounded-md px-4 py-3 mb-6 text-sm" data-testid="fee-external">
          <div className="flex justify-between py-1"><span className="text-ink-tertiary">You send via {method === "zelle" ? "Zelle" : "Venmo"} (directly)</span><span className="font-bold">{money(effectiveAmount)}</span></div>
          <div className="flex justify-between py-1"><span className="text-ink-tertiary">Platform fee (card, now)</span><span className="font-bold">{money(EXTERNAL_FEE)}</span></div>
          <div className="flex justify-between py-1 border-t border-border mt-1 pt-2"><span className="font-black">{recipient.name.split(" ")[0]} receives</span><span className="font-black">{money(effectiveAmount)}</span></div>
          <p className="text-xs text-ink-tertiary mt-2">Pay the ${EXTERNAL_FEE} card fee now, then we reveal their {method === "zelle" ? "Zelle" : "Venmo"} QR so you can send {money(effectiveAmount)} directly.</p>
        </div>
      ) : (
        <div className="bg-surface-2 border border-border rounded-md px-4 py-3 mb-6 text-sm">
          <div className="flex justify-between py-1"><span className="text-ink-tertiary">Your gift (you pay)</span><span className="font-bold">{money(effectiveAmount)}</span></div>
          <div className="flex justify-between py-1"><span className="text-ink-tertiary">Platform fee{effectiveAmount >= 100 ? " (2.9%)" : ""}</span><span className="font-bold">−{money(fee)}</span></div>
          <div className="flex justify-between py-1 border-t border-border mt-1 pt-2"><span className="font-black">{recipient.name.split(" ")[0]} receives</span><span className="font-black">{money(Math.max(0, effectiveAmount - fee))}</span></div>
        </div>
      )}

      <Button
        data-testid="donate-send-button"
        disabled={sending}
        onClick={send}
        className="w-full h-14 rounded-pill font-black text-base bg-brand-primary hover:opacity-90 text-white"
      >
        {sending ? <Loader2 className="animate-spin" size={20} /> : <><HeartHandshake size={20} className="mr-1" /> {isExternal ? `Send ${money(effectiveAmount)} via ${method === "zelle" ? "Zelle" : "Venmo"}` : `Send ${money(effectiveAmount)}`} <ExternalLink size={16} className="ml-1 opacity-80" /></>}
      </Button>
      <p className="text-center text-xs text-ink-tertiary mt-3">Secure checkout by Stripe</p>
    </div>
  );
}
